/**
 * US 34308: the context a help desk ticket carries so HaloITSM agents can triage
 * without calling the user back (which app, which version, who, where, on what).
 * Sent as `clientContext` on POST api/Helpdesk/createTicket; the Software Security
 * API lists each non-empty field in a "Submitted from" section of the ticket body.
 * Pure and platform-agnostic: the web and NativeScript modals supply the device half.
 */

export type HelpdeskDeviceType = 'phone' | 'tablet' | 'computer';

export interface HelpdeskDeviceInfo {
  deviceType?: HelpdeskDeviceType;
  /** Web only, e.g. "Chrome 128.0". */
  browser?: string;
  /** e.g. "Windows 10/11", "iOS 18". */
  os?: string;
  /** Mobile only, e.g. "Apple iPhone15,2". */
  deviceModel?: string;
}

export interface HelpdeskClientContext extends HelpdeskDeviceInfo {
  appName?: string;
  appVersion?: string;
  userName?: string;
  userEmail?: string;
  route?: string;
  jobNumber?: string;
  companyId?: string;
}

export interface HelpdeskClientContextInput {
  appName?: string | null;
  appVersion?: string | null;
  userName?: string | null;
  userEmail?: string | null;
  route?: string | null;
  /** A page-supplied job wins over the one parsed from the route. */
  jobNumber?: string | number | null;
  /** A page-supplied company wins over the one parsed from the route. */
  companyId?: string | number | null;
  device: HelpdeskDeviceInfo;
}

const JOB_PATH_SEGMENTS = new Set(['job', 'jobs']);
const JOB_QUERY_KEYS = new Set(['job', 'jobid', 'jobnumber', 'jobno']);
const COMPANY_QUERY_KEYS = new Set([
  'company',
  'companyid',
  'companynumber',
  'companyno',
]);
// A job id always carries a digit; this keeps /jobs/new or /jobs/list from reading as a job.
const LOOKS_LIKE_JOB = /\d/;
// Query keys whose values are credentials (reset tokens, auth codes, API keys).
// Short names (code, sig, key) match exactly so costCode or zipCode survive.
const SECRET_QUERY_KEY =
  /^(code|sig|signature|key|apikey|api_key|auth)$|token|password|passwd|secret/i;
// The web build leaves `__APP_VERSION__` unreplaced when run locally.
const BUILD_PLACEHOLDER = /^__[A-Z_]+__$/;

/** Job and company the user had in scope, read from the route path and query string. */
export function parseHelpdeskRouteScope(route: string | null | undefined): {
  jobNumber?: string;
  companyId?: string;
} {
  if (!route) {
    return {};
  }

  const [path, query = ''] = route.split('#')[0].split('?');
  const scope: { jobNumber?: string; companyId?: string } = {};

  const segments = path.split('/').filter(Boolean);
  for (let i = 0; i < segments.length - 1; i++) {
    const next = safeDecode(segments[i + 1]);
    if (
      JOB_PATH_SEGMENTS.has(segments[i].toLowerCase()) &&
      LOOKS_LIKE_JOB.test(next)
    ) {
      scope.jobNumber = next;
      break;
    }
  }

  for (const pair of query.split('&')) {
    const [rawKey, rawValue = ''] = pair.split('=');
    const key = safeDecode(rawKey).toLowerCase();
    const value = safeDecode(rawValue.replace(/\+/g, ' ')).trim();
    if (!value) {
      continue;
    }
    if (
      !scope.jobNumber &&
      JOB_QUERY_KEYS.has(key) &&
      LOOKS_LIKE_JOB.test(value)
    ) {
      scope.jobNumber = value;
    } else if (!scope.companyId && COMPANY_QUERY_KEYS.has(key)) {
      scope.companyId = value;
    }
  }

  return scope;
}

/**
 * The app name a ticket reports. The base environment sets `displayName: ''` and most
 * web apps only set `name`, so a blank displayName counts as missing (`??` would keep it).
 */
export function helpdeskAppName(env: {
  displayName?: string | null;
  name?: string | null;
}): string | undefined {
  return clean(env.displayName) ?? clean(env.name);
}

/** The context object both help desk modals send; blank values are dropped. */
export function buildHelpdeskClientContext(
  input: HelpdeskClientContextInput
): HelpdeskClientContext {
  const routeScope = parseHelpdeskRouteScope(input.route);
  const context: HelpdeskClientContext = {
    appName: clean(input.appName),
    appVersion: clean(input.appVersion),
    userName: clean(input.userName),
    userEmail: clean(input.userEmail),
    route: redactRouteSecrets(clean(input.route)),
    jobNumber: clean(input.jobNumber) ?? routeScope.jobNumber,
    companyId: clean(input.companyId) ?? routeScope.companyId,
    deviceType: input.device.deviceType,
    browser: clean(input.device.browser),
    os: clean(input.device.os),
    deviceModel: clean(input.device.deviceModel),
  };

  return Object.fromEntries(
    Object.entries(context).filter(([, value]) => value !== undefined)
  ) as HelpdeskClientContext;
}

/** The subset of NativeScript's `Device` the mapping reads, so it stays testable off-device. */
export interface NativeDeviceLike {
  deviceType?: string;
  manufacturer?: string;
  model?: string;
  os?: string;
  osVersion?: string;
}

/** Map NativeScript `Device` values to the ticket's device fields. */
export function nativeHelpdeskDevice(
  device: NativeDeviceLike
): HelpdeskDeviceInfo {
  const manufacturer = clean(device.manufacturer);
  const model = clean(device.model);
  const deviceModel =
    manufacturer &&
    model &&
    !model.toLowerCase().startsWith(manufacturer.toLowerCase())
      ? `${manufacturer} ${model}`
      : (model ?? manufacturer);
  const os = [clean(device.os), clean(device.osVersion)]
    .filter(Boolean)
    .join(' ');

  const info: HelpdeskDeviceInfo = {
    deviceType:
      device.deviceType?.toLowerCase() === 'tablet' ? 'tablet' : 'phone',
    deviceModel,
    os: os || undefined,
  };
  return Object.fromEntries(
    Object.entries(info).filter(([, value]) => value !== undefined)
  ) as HelpdeskDeviceInfo;
}

/**
 * The route lands in a ticket the whole support queue can read, so a query value
 * whose key names a credential is replaced before it leaves the app. A fragment
 * can carry tokens too (`#access_token=...`, hash-routed reset links) and support
 * never needs it, so it is dropped outright rather than pattern-matched.
 */
function redactRouteSecrets(route: string | undefined): string | undefined {
  if (!route) {
    return route;
  }
  const hashAt = route.indexOf('#');
  const withoutFragment =
    hashAt === -1 ? route : `${route.slice(0, hashAt)}#redacted`;
  if (!withoutFragment.includes('?')) {
    return withoutFragment;
  }
  return withoutFragment.replace(
    /([?&])([^=&#]+)=([^&#]*)/g,
    (match, sep: string, key: string) =>
      SECRET_QUERY_KEY.test(safeDecode(key)) ? `${sep}${key}=redacted` : match
  );
}

function clean(value: string | number | null | undefined): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  const text = `${value}`.trim();
  return text && !BUILD_PLACEHOLDER.test(text) ? text : undefined;
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}
